import { ReactNode } from 'react'
import { useController } from 'react-hook-form'

import { FocusTarget } from '@/components/form'
import { ErrorMessage } from '@/components/ui/error-message'
import { Label } from '@/components/ui/label'
import { theme } from '@/theme/theme'

import * as S from './visual-fields.styled'

interface IconFieldProps {
  color: string
  icons: string[]
  label: string
  name?: string
  renderIcon: (icon: string) => ReactNode
  required?: boolean
}

export const IconField = ({
  color,
  icons,
  label,
  name = 'icon',
  renderIcon,
  required = true,
}: IconFieldProps) => {
  const { field, fieldState } = useController({ name })

  return (
    <S.Field>
      <Label required={required}>{label}</Label>
      <FocusTarget fieldRef={field.ref}>
        <S.Options role="radiogroup">
          {icons.map((icon) => (
            <S.IconOption
              key={icon}
              $color={color || theme.colors.black700}
              $selected={field.value === icon}
              aria-checked={field.value === icon}
              aria-label={icon}
              role="radio"
              type="button"
              onClick={() => field.onChange(icon)}
            >
              {renderIcon(icon)}
            </S.IconOption>
          ))}
        </S.Options>
      </FocusTarget>
      <S.ErrorSlot>
        <ErrorMessage>{fieldState.error?.message}</ErrorMessage>
      </S.ErrorSlot>
    </S.Field>
  )
}

interface ColorFieldProps {
  colors: string[]
  label: string
  name?: string
  required?: boolean
}

export const ColorField = ({ colors, label, name = 'color', required = true }: ColorFieldProps) => {
  const { field, fieldState } = useController({ name })

  return (
    <S.Field>
      <Label required={required}>{label}</Label>
      <FocusTarget fieldRef={field.ref}>
        <S.Options role="radiogroup">
          {colors.map((color) => (
            <S.ColorOption
              key={color}
              $color={color}
              $selected={field.value === color}
              aria-checked={field.value === color}
              aria-label={color}
              role="radio"
              type="button"
              onClick={() => field.onChange(color)}
            />
          ))}
        </S.Options>
      </FocusTarget>
      <S.ErrorSlot>
        <ErrorMessage>{fieldState.error?.message}</ErrorMessage>
      </S.ErrorSlot>
    </S.Field>
  )
}
